import React, { useState, useEffect } from 'react'
import {
  Card, Form, Button, Select, InputNumber, Input,
  Divider, message, Checkbox, Table, Tag, Tooltip,
} from 'antd'
import {
  ArrowLeftOutlined, InfoCircleOutlined, CheckCircleOutlined, FallOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useMasterData } from '@/context/MasterDataContext'
import { paymentStore } from './paymentStore'
import { invoiceStore, Invoice } from '@/pages/Invoice/invoiceStore'
import { generateCode } from '@/utils/codeGenerator'
import { NotificationEngine } from '@/utils/notificationEngine'
import { applyPaymentToInvoice, reversePaymentFromInvoice } from '@/utils/invoiceHelpers'
import DocumentUploader from '@/components/DocumentUploader'

interface OpenInvoice {
  key: string
  invoiceNumber: string
  period: string
  totalAmount: number
  paidAmount: number
  outstanding: number
  baseAmount: number  // the invoice's own base (pre-GST) amount
  gstAmount: number   // the invoice's own GST amount
}

const fmt = (n: number) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// An invoice isn't final/collectable until it's out of draft/rejected review
// and hasn't been cancelled — same rule PaymentList.tsx's Outstanding summary
// uses. Without this, a raw auto-generated draft (not yet reviewed/issued)
// showed up as payable here even though it never appeared as outstanding on
// the Payment list, so the two screens silently disagreed with each other.
const isCollectable = (i: Invoice) =>
  i.status !== 'draft' && i.status !== 'rejected' && i.status !== 'cancelled'

// Open (unpaid) consumer invoices for a customer — from the real invoice
// store. Selecting a row always applies its full outstanding balance; there's
// no separate amount to type in.
const openInvoicesFor = (customerId: string): OpenInvoice[] =>
  invoiceStore.getAll()
    .filter(i => i.type === 'consumer' && i.entityId === customerId && isCollectable(i) && (Number(i.totalAmount) || 0) - (Number(i.paidAmount) || 0) > 0)
    .map(i => ({
      key: i.id,
      invoiceNumber: i.invoiceNumber,
      period: i.period,
      totalAmount: Number(i.totalAmount) || 0,
      paidAmount: Number(i.paidAmount) || 0,
      outstanding: (Number(i.totalAmount) || 0) - (Number(i.paidAmount) || 0),
      baseAmount: Number(i.baseAmount) || 0,
      gstAmount: Number(i.gstAmount) || 0,
    }))

const outstandingFor = (customerId: string): number =>
  invoiceStore.getAll()
    .filter(i => i.type === 'consumer' && i.entityId === customerId && isCollectable(i))
    .reduce((s, i) => s + Math.max(0, (Number(i.totalAmount) || 0) - (Number(i.paidAmount) || 0)), 0)

const PaymentForm: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isEditing = !!id

  const { consumers } = useMasterData()
  const [, setTick] = useState(0)
  useEffect(() => { invoiceStore.refresh().then(() => setTick(t => t + 1)) }, [])

  const customerOptions = consumers
    .filter(c => c.status === 'active')
    .map(c => ({
      label:       `${c.name} (${c.code})`,
      value:       c.id,
      outstanding: outstandingFor(c.id),
    }))

  const [selectedCustomer, setSelectedCustomer] = useState<string>('')
  const [invoiceRows, setInvoiceRows] = useState<OpenInvoice[]>([])
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [tdsAmount, setTdsAmount] = useState<number>(0)
  const [saving, setSaving] = useState(false)

  // Base/GST auto-fill from the selected invoice(s) (see computedBase/
  // computedGst below) but stay editable — for a partial or adjusted
  // settlement. Null = "use the computed default".
  const [baseOverride, setBaseOverride] = useState<number | null>(null)
  const [gstOverride, setGstOverride] = useState<number | null>(null)

  const handleCustomerChange = (val: string) => {
    setSelectedCustomer(val)
    setInvoiceRows(openInvoicesFor(val))
    setSelectedKeys([])
    setBaseOverride(null)
    setGstOverride(null)
    form.setFieldValue('customerId', val)
  }

  // Clears a manual Base/GST override when the operator actually changes
  // which invoices are checked — a stale override from a different invoice
  // shouldn't silently carry over. Called from rowSelection.onChange, NOT a
  // useEffect keyed on selectedKeys: the edit-mode loader below also calls
  // setSelectedKeys (to restore this payment's own selection) and immediately
  // sets the overrides from the saved record — a reactive effect would fire
  // right after and wipe that back to null. Only a genuine checkbox click
  // should reset it.
  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedKeys(keys as string[])
    setBaseOverride(null)
    setGstOverride(null)
  }

  // Coming from a specific Outstanding row's "Record Payment" button
  // (PaymentList.tsx passes customerId in nav state) — that already tells us
  // exactly who, so pull in every one of that customer's open invoices
  // pre-selected instead of making the operator pick the customer and then
  // each invoice by hand again.
  //
  // Coming from a specific Invoice's "Record Payment" button instead
  // (InvoiceDetail.tsx passes invoiceId) — same idea, but pre-select just
  // that one invoice rather than every open invoice for its customer, since
  // that's the specific bill the operator is trying to settle. This is the
  // ONE payment-recording flow both entry points share.
  const navInvoiceId = (location.state as { invoiceId?: string } | null)?.invoiceId
  useEffect(() => {
    if (isEditing) return
    const navCustomerId = (location.state as { customerId?: string } | null)?.customerId
    if (navInvoiceId) {
      const inv = invoiceStore.get(navInvoiceId)
      if (!inv?.entityId) return
      const rows = openInvoicesFor(inv.entityId)
      setSelectedCustomer(inv.entityId)
      setInvoiceRows(rows)
      setSelectedKeys(rows.some(r => r.key === navInvoiceId) ? [navInvoiceId] : [])
      form.setFieldValue('customerId', inv.entityId)
      return
    }
    if (!navCustomerId) return
    const rows = openInvoicesFor(navCustomerId)
    setSelectedCustomer(navCustomerId)
    setInvoiceRows(rows)
    setSelectedKeys(rows.map(r => r.key))
    form.setFieldValue('customerId', navCustomerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Came here from a specific invoice's "Record Payment" button → go back to
  // that invoice instead of the Payments list once done (or when cancelling).
  const returnTo = (!isEditing && navInvoiceId) ? `/invoices/${navInvoiceId}` : '/payments'

  const selectedRows = invoiceRows.filter(r => selectedKeys.includes(r.key))
  const singleInvoice = selectedKeys.length === 1 ? selectedRows[0] : null

  // Base and GST default to the selected invoice(s)' own numbers (prorated to
  // whatever's still outstanding, in case one was already partly paid
  // before) the moment an invoice is checked — but both stay editable below
  // for a partial or adjusted settlement.
  const computedGst = selectedRows.reduce((s, r) => s + (r.totalAmount > 0 ? (r.gstAmount / r.totalAmount) * r.outstanding : 0), 0)
  const computedOutstandingTotal = selectedRows.reduce((s, r) => s + r.outstanding, 0)
  const computedBase = Math.max(0, computedOutstandingTotal - computedGst)
  const gstAmount = gstOverride ?? computedGst
  const baseAmount = baseOverride ?? computedBase
  const isOverridden = baseOverride != null || gstOverride != null

  // The amount actually applied to clear the invoice: with exactly one
  // invoice selected there's no ambiguity about which one a manual Base/GST
  // entry belongs to, so an override drives what settles that invoice too
  // (capped at what's actually outstanding) — the same "Base + GST settles
  // the invoice" behaviour a single-invoice payment has always had. With more
  // than one invoice selected, an override is for record-keeping only and
  // doesn't redistribute across them — each still settles its own full
  // outstanding balance.
  const receiptTotal = (singleInvoice && isOverridden)
    ? Math.min(Math.max(0, baseAmount + gstAmount), singleInvoice.outstanding)
    : computedOutstandingTotal

  // Total Amount Received = Base + GST − TDS.
  const netPayable = baseAmount + gstAmount - tdsAmount
  const customerObj = customerOptions.find(c => c.value === selectedCustomer)

  useEffect(() => {
    if (!id) return
    const existing = paymentStore.get(id)
    if (!existing) return

    setSelectedCustomer(existing.customerId)
    setTdsAmount(existing.tds)

    // Start from the customer's currently-open invoices, then add back any
    // invoice this payment applied to that's since been fully cleared (and so
    // no longer shows up as "open") so it's still visible/selected here.
    const openRows = openInvoicesFor(existing.customerId)
    const extraRows: OpenInvoice[] = existing.invoices
      .filter(a => !openRows.some(r => r.key === a.invoiceId))
      .map(a => {
        const inv = invoiceStore.get(a.invoiceId)
        return inv ? {
          key: inv.id, invoiceNumber: inv.invoiceNumber, period: inv.period,
          totalAmount: Number(inv.totalAmount) || 0, paidAmount: Number(inv.paidAmount) || 0,
          // Add this payment's own previously-applied amount back into the
          // outstanding shown here — editing reverses the old application
          // before re-applying (see onFinish), so that's what's genuinely
          // available to re-select.
          outstanding: Math.max(0, (Number(inv.totalAmount) || 0) - (Number(inv.paidAmount) || 0)) + a.applied,
          baseAmount: Number(inv.baseAmount) || 0,
          gstAmount: Number(inv.gstAmount) || 0,
        } : null
      })
      .filter((r): r is OpenInvoice => r !== null)
    setInvoiceRows([...openRows, ...extraRows])
    setSelectedKeys(existing.invoices.map(a => a.invoiceId))
    // An edited record's own saved Base/GST are the starting point, not
    // whatever the fresh computed default happens to be.
    setBaseOverride(existing.baseAmount ?? null)
    setGstOverride(existing.gstAmount ?? null)

    form.setFieldsValue({
      customerId: existing.customerId,
      receiptNumber: existing.receiptNumber,
      mode: existing.mode,
      paymentDate: existing.date,
      tdsAmount: existing.tds,
      reference: existing.reference,
      tdsCertRef: existing.tdsCertRef,
      tdsCertUrl: existing.tdsCertUrl,
      form16a: existing.form16a,
      form16aAttached: existing.form16aAttached,
      gstCertRef: existing.gstCertRef,
      gstCertUrl: existing.gstCertUrl,
    })
  }, [id])

  const onFinish = async () => {
    if (saving) return   // guard against a double-click firing this twice
    if (!selectedCustomer) { message.error('Select a customer'); return }
    if (selectedKeys.length === 0) { message.error('Select at least one invoice to settle'); return }

    setSaving(true)
    try {

    const values = form.getFieldsValue()
    const consumer = consumers.find(c => c.id === selectedCustomer)
    const existingCount = paymentStore.getAll().length

    // Reconcile against the actual invoices: reverse whatever this payment
    // previously applied (edit only), then apply each selected invoice's
    // full outstanding balance — except the one invoice a Base/GST override
    // targets (see receiptTotal above), which settles for that overridden
    // amount instead.
    if (isEditing && id) {
      const previous = paymentStore.get(id)
      previous?.invoices.forEach(a => reversePaymentFromInvoice(a.invoiceId, a.applied))
    }
    const appliedInvoices = selectedRows.map(r => {
      const amountToApply = (singleInvoice && isOverridden && r.key === singleInvoice.key) ? receiptTotal : r.outstanding
      return {
        invoiceId: r.key,
        invoiceNumber: r.invoiceNumber,
        applied: applyPaymentToInvoice(r.key, amountToApply),
      }
    })

    // Collection status of the invoice(s) after applying this receipt.
    const outstanding = customerObj?.outstanding ?? 0
    const settled = receiptTotal + tdsAmount   // gross applied + TDS clears the invoice
    let paymentStatus: 'unpaid' | 'partially_paid' | 'paid' | 'tds_deducted' | 'overdue'
    if (outstanding <= 0 || settled >= outstanding) {
      paymentStatus = tdsAmount > 0 && receiptTotal < outstanding ? 'tds_deducted' : 'paid'
    } else if (settled > 0) {
      paymentStatus = 'partially_paid'
    } else {
      paymentStatus = 'unpaid'
    }

    let receiptNum = values.receiptNumber || ''
    if (!isEditing && !receiptNum) {
      try { receiptNum = await generateCode('payments') } catch { receiptNum = `RCP-${new Date().getFullYear()}-${String(existingCount + 1).padStart(3, '0')}` }
    }

    const record = {
      receiptNumber: receiptNum || `RCP-${new Date().getFullYear()}-${String(existingCount + 1).padStart(3, '0')}`,
      customer: consumer?.name ?? '',
      customerId: selectedCustomer,
      invoices: appliedInvoices,
      baseAmount,
      gstAmount,
      amount: receiptTotal,
      tds: tdsAmount,
      amountReceived: netPayable,
      mode: values.mode,
      date: values.paymentDate || new Date().toISOString().slice(0, 10),
      status: 'confirmed' as const,
      paymentStatus,
      reference: values.reference || undefined,
      tdsCertRef: values.tdsCertRef || undefined,
      tdsCertUrl: values.tdsCertUrl || undefined,
      form16a: values.form16a || undefined,
      form16aAttached: !!values.form16aAttached,
      gstCertRef: values.gstCertRef || undefined,
      gstCertUrl: values.gstCertUrl || undefined,
    }

    const saved = paymentStore.save({ id: isEditing ? id : undefined, ...record })

    if (!isEditing) {
      NotificationEngine.trigger({
        event: 'PAYMENT_RECEIVED',
        title: `Payment Received: ${saved.receiptNumber}`,
        message: `Payment of ₹${saved.amount.toLocaleString('en-IN')} received from ${saved.customer} via ${saved.mode} for invoices: ${saved.invoices.map(a => a.invoiceNumber).join(', ')}.`,
        recipient: 'Accounts Team',
      })
    }

    message.success(`Payment receipt recorded. ${appliedInvoices.length} invoice(s) updated.`)
    navigate(returnTo)
    } finally {
      setSaving(false)
    }
  }

  const invoiceColumns = [
    {
      title: 'Invoice #',
      key: 'number',
      render: (_: any, r: OpenInvoice) => (
        <div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 12, color: '#001A4D' }}>{r.invoiceNumber}</div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.period}</div>
        </div>
      ),
    },
    {
      title: 'Base Amount',
      dataIndex: 'baseAmount',
      render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{fmt(v)}</span>,
    },
    {
      title: 'GST Amount',
      dataIndex: 'gstAmount',
      render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{fmt(v)}</span>,
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      render: (v: number) => <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 600 }}>{fmt(v)}</span>,
    },
    {
      title: 'Outstanding',
      dataIndex: 'outstanding',
      render: (v: number) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#DC2626', fontSize: 13 }}>{fmt(v)}</span>
      ),
    },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(returnTo)} />
        <div>
          <h2 className="page-title">{isEditing ? 'Edit Payment' : 'Record Payment'}</h2>
          <p className="page-subtitle">Select the invoice(s) being settled — the amounts fill in automatically.</p>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish}>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Form.Item label="Customer" name="customerId" style={{ flex: 2, minWidth: 260, marginBottom: 0 }} rules={[{ required: true }]}>
              <Select
                placeholder="Select customer..."
                options={customerOptions}
                onChange={handleCustomerChange}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item label="Receipt Number" name="receiptNumber" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
              <Input placeholder="Auto: RCP-2025-006" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
            </Form.Item>
          </div>

          {invoiceRows.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Select the invoice(s) to settle</div>
              <Table
                dataSource={invoiceRows}
                columns={invoiceColumns}
                rowKey="key"
                pagination={false}
                scroll={{ x: true }}
                size="small"
                rowSelection={{
                  selectedRowKeys: selectedKeys,
                  onChange: handleSelectionChange,
                }}
              />
            </div>
          )}
        </Card>

        {/* ── Amount & TDS — Base/GST show automatically once an invoice is
            selected above; TDS is the one thing to add. ──────────────── */}
        {receiptTotal > 0 && (
          <Card style={{ marginBottom: 16 }}>
            {singleInvoice && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 2px', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: '#4A5568' }}>Invoice</span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 700, color: '#001A4D' }}>{singleInvoice.invoiceNumber}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontSize: 12, color: '#4A5568', marginBottom: 6 }}>
                  Base Amount
                  <Tooltip title="Defaults to the selected invoice(s)' own base amount — editable for a partial or adjusted settlement.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8A93A6' }} />
                  </Tooltip>
                </div>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0} step={100} precision={2} prefix="₹"
                  value={baseAmount}
                  onChange={v => setBaseOverride(v ?? 0)}
                />
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontSize: 12, color: '#4A5568', marginBottom: 6 }}>
                  GST Amount
                  <Tooltip title="Defaults to the selected invoice(s)' own GST — editable.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8A93A6' }} />
                  </Tooltip>
                </div>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0} step={10} precision={2} prefix="₹"
                  value={gstAmount}
                  onChange={v => setGstOverride(v ?? 0)}
                />
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontSize: 12, color: '#4A5568', marginBottom: 6 }}>
                  TDS Deducted
                  <Tooltip title="PY-05: TDS deducted by the customer, reconciled against Form 16A.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8A93A6' }} />
                  </Tooltip>
                </div>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0} step={0.01} precision={2} prefix="₹"
                  value={tdsAmount}
                  onChange={v => setTdsAmount(v ?? 0)}
                />
              </div>
            </div>

            {selectedKeys.length > 1 && isOverridden && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
                <InfoCircleOutlined style={{ marginRight: 6 }} />
                Multiple invoices are selected, so this Base/GST edit is for the receipt's own record only — each invoice below still settles for its full outstanding balance.
              </div>
            )}

            {/* Total shown only after Base/GST/TDS */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '14px 16px', marginTop: 16, background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, color: '#065F46' }}>
                <CheckCircleOutlined />
                Total Amount Received / Credited
              </span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 22, fontWeight: 800, color: '#059669' }}>{fmt(netPayable)}</span>
            </div>
            {tdsAmount > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#8A93A6' }}>
                <FallOutlined style={{ marginRight: 4 }} />
                Base + GST − TDS ({fmt(tdsAmount)}) = {fmt(netPayable)}
              </div>
            )}
          </Card>
        )}

        {/* ── Payment Details ──────────────────────────────── */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Form.Item label="Payment Mode" name="mode" rules={[{ required: true }]} style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
              <Select
                placeholder="Select mode"
                options={['Cash', 'Cheque', 'NEFT', 'RTGS', 'UPI', 'IMPS'].map(m => ({ label: m, value: m }))}
              />
            </Form.Item>
            <Form.Item label="Payment Date" name="paymentDate" rules={[{ required: true }]} style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
              <Input type="date" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
            </Form.Item>
            <Form.Item label="Reference / UTR #" name="reference" style={{ flex: 2, minWidth: 200, marginBottom: 0 }}>
              <Input placeholder="Transaction / UTR number" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
            </Form.Item>
          </div>
        </Card>

        {/* ── TDS & GST Certificates (PY-05) ──── */}
        <Card
          title={
            <span style={{ fontWeight: 700 }}>
              TDS &amp; GST Certificates
              <Tag style={{ marginLeft: 8, fontSize: 11 }}>PY-05</Tag>
            </span>
          }
          style={{ marginBottom: 16 }}
        >
          <div style={{ fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            TDS Certificate
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            <Form.Item label="TDS Certificate Ref" name="tdsCertRef" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
              <Input placeholder="Certificate reference" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
            </Form.Item>
            <Form.Item label="Form 16A Reference" name="form16a" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
              <Input placeholder="Form 16A reference number" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
            <Form.Item name="tdsCertUrl" label="TDS Certificate File" style={{ marginBottom: 0 }}>
              <DocumentUploader folder="payments" />
            </Form.Item>
            <Form.Item name="form16aAttached" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox><span style={{ fontWeight: 600 }}>Form 16A received and attached</span></Checkbox>
            </Form.Item>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <div style={{ fontWeight: 700, fontSize: 12, color: '#1E40AF', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            GST Certificate
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            <Form.Item label="GST Certificate Ref" name="gstCertRef" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
              <Input placeholder="Certificate / document reference" style={{ fontFamily: 'JetBrains Mono, monospace' }} />
            </Form.Item>
          </div>
          <Form.Item name="gstCertUrl" label="GST Certificate File" style={{ marginBottom: 0 }}>
            <DocumentUploader folder="payments" />
          </Form.Item>
        </Card>

        <Divider />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button onClick={() => navigate(returnTo)}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>{isEditing ? 'Update' : 'Record Payment'}</Button>
        </div>
      </Form>
    </div>
  )
}

export default PaymentForm
